import { CopyOutlined, RedoOutlined } from "@ant-design/icons";
import {
    Alert,
    Button,
    ConfigProvider,
    Descriptions,
    Modal,
    Popconfirm,
    Space,
    Tooltip,
    message,
} from "antd";
import { Dispatch, SetStateAction, useState } from "react";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import {
    PaymentOutboxItemModel,
    PaymentOutboxStatus,
} from "@/core/models/payment-outbox.model";
import { PaymentOutboxRepo } from "@/core/repos/tables/payment-outbox.repo";
import container from "@/lib/di/container";
import { formatDateTimePipe } from "@/core/pipes/format-date.pipe";
import { formatCurrencyPipeText } from "@/core/pipes/format-currency.pipe";
import {
    paymentOutboxStatusPipe,
    paymentOutboxStatusTagPipe,
} from "@/core/pipes/payment-outbox-status.pipe";
import AuthRepo from "@/core/repos/auth.repo";
import { GlobalUtils } from "@/app/home/utils/global.utils";
import {
    PERMISSIONS,
    PermissionClaim,
} from "@/core/enums/permissions.enum";

const paymentOutboxRepo = container.get(PaymentOutboxRepo);
const authRepo = container.get(AuthRepo);

interface DetailModalProps {
    isModalOpen: boolean;
    setIsModalOpen: Dispatch<SetStateAction<boolean>>;
    data: PaymentOutboxItemModel;
    canRetry: boolean;
}

export const DetailModal: React.FC<DetailModalProps> = ({
    isModalOpen,
    setIsModalOpen,
    data,
    canRetry,
}) => {
    const claims = authRepo.useClaims() as PermissionClaim[];
    const [loading, setLoading] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();

    const handleCancel = () => setIsModalOpen(false);

    const handleCopy = (value: string | null | undefined) => {
        if (!value) return;
        navigator.clipboard.writeText(value).then(() => {
            messageApi.success("Đã sao chép vào bộ nhớ tạm!");
        }).catch(() => {
            messageApi.error("Sao chép thất bại!");
        });
    };

    const handleRetry = async () => {
        setLoading(true);
        const result = await paymentOutboxRepo.retryDead([data.id]);
        if (result) {
            messageApi.success(
                `Đã đưa ${result.retried} giao dịch vào hàng đợi để xử lý lại`
            );
            setIsModalOpen(false);
        } else {
            messageApi.error("Thử lại thất bại - vui lòng kiểm tra quyền hoặc trạng thái");
        }
        setLoading(false);
    };

    const isDead = data.status === PaymentOutboxStatus.Dead;
    const showRetryButton =
        canRetry &&
        isDead &&
        GlobalUtils.checkPerm(claims, PERMISSIONS.PAYMENT_OUTBOX_RETRY.key);

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal
                open={isModalOpen}
                onCancel={handleCancel}
                width={900}
                centered
                styles={{
                    mask: {
                        backdropFilter: "blur(2px)",
                        backgroundColor: "var(--primary-mask)",
                    },
                }}
                footer={[
                    <Space key="footer" className="w-full flex justify-end">
                        {showRetryButton && (
                            <Popconfirm
                                title="Thử lại giao dịch này?"
                                description="Giao dịch sẽ được đưa về trạng thái Pending và worker sẽ xử lý lại trong < 2 giây."
                                onConfirm={handleRetry}
                                okText="Thử lại"
                                cancelText="Huỷ"
                                okButtonProps={{ danger: false }}
                            >
                                <Button
                                    type="primary"
                                    icon={<RedoOutlined />}
                                    loading={loading}
                                    style={{ background: "var(--warning-button)" }}
                                >
                                    Thử lại
                                </Button>
                            </Popconfirm>
                        )}
                        <Button onClick={handleCancel}>Đóng</Button>
                    </Space>,
                ]}
            >
                <div className="flex flex-row w-full items-center justify-start select-none mb-4">
                    <p className="ml-2 text-2xl font-bold text-primary">
                        CHI TIẾT GIAO DỊCH PAYMENTOUTBOX
                    </p>
                </div>

                <Descriptions
                    bordered
                    column={2}
                    size="small"
                    labelStyle={{ width: 180, background: "#fafafa" }}
                    items={[
                        {
                            key: "id",
                            label: "ID",
                            children: (
                                <div className="flex items-center gap-1">
                                    <span
                                        className="truncate inline-block max-w-[280px] align-middle"
                                        title={data.id}
                                    >
                                        {data.id}
                                    </span>
                                    <Tooltip title="Sao chép">
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<CopyOutlined />}
                                            onClick={() => handleCopy(data.id)}
                                        />
                                    </Tooltip>
                                </div>
                            ),
                        },
                        {
                            key: "status",
                            label: "Trạng thái",
                            children: paymentOutboxStatusTagPipe(data.status, {
                                width: "auto",
                                minWidth: "auto",
                                display: "inline-block",
                            }),
                        },
                        {
                            key: "channel",
                            label: "Kênh thanh toán",
                            children: data.channel || "-",
                        },
                        {
                            key: "amount",
                            label: "Số tiền (VNĐ)",
                            children: (
                                <span className="font-semibold">
                                    {formatCurrencyPipeText(data.amount)}
                                </span>
                            ),
                        },
                        {
                            key: "requestId",
                            label: "RequestId",
                            children: (
                                <div className="flex items-center gap-1">
                                    <span>{data.requestId || "-"}</span>
                                    {data.requestId && (
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<CopyOutlined />}
                                            onClick={() => handleCopy(data.requestId)}
                                        />
                                    )}
                                </div>
                            ),
                        },
                        {
                            key: "bankTransactionId",
                            label: "BankTransactionId",
                            children: (
                                <div className="flex items-center gap-1">
                                    <span>{data.bankTransactionId || "-"}</span>
                                    {data.bankTransactionId && (
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<CopyOutlined />}
                                            onClick={() =>
                                                handleCopy(data.bankTransactionId)
                                            }
                                        />
                                    )}
                                </div>
                            ),
                        },
                        {
                            key: "orderId",
                            label: "OrderId",
                            children: data.orderId || "-",
                        },
                        {
                            key: "invoice",
                            label: "Mã hoá đơn",
                            children: data.invoiceCode
                                ? `${data.invoiceCode}${
                                      data.invoiceStatus ? ` (${data.invoiceStatus})` : ""
                                  }`
                                : "-",
                        },
                        {
                            key: "waterFactory",
                            label: "Nhà máy",
                            children: data.waterFactoryName || "-",
                        },
                        {
                            key: "attemptCount",
                            label: "Số lần thử",
                            children: (
                                <span>
                                    {data.attemptCount}
                                    {isDead ? " (đã đạt giới hạn)" : ""}
                                </span>
                            ),
                        },
                        {
                            key: "createdAt",
                            label: "Tạo lúc",
                            children: data.createdAt
                                ? formatDateTimePipe(data.createdAt)
                                : "-",
                        },
                        {
                            key: "processedAt",
                            label: "Xử lý xong",
                            children: data.processedAt
                                ? formatDateTimePipe(data.processedAt)
                                : "-",
                        },
                        {
                            key: "nextAttemptAt",
                            label: "Lần thử kế tiếp",
                            children: data.nextAttemptAt
                                ? formatDateTimePipe(data.nextAttemptAt)
                                : "-",
                        },
                        {
                            key: "durationMs",
                            label: "Thời gian xử lý",
                            children: data.durationMs != null
                                ? `${data.durationMs.toLocaleString()} ms`
                                : "-",
                        },
                    ]}
                />

                {data.lastError && (
                    <div className="mt-4">
                        <p className="text-[15px] font-semibold mb-1">
                            Lỗi gần nhất
                        </p>
                        <Alert
                            type="error"
                            showIcon
                            message={
                                <pre
                                    style={{
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                        margin: 0,
                                        fontFamily:
                                            "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                                        fontSize: 13,
                                    }}
                                >
                                    {data.lastError}
                                </pre>
                            }
                        />
                    </div>
                )}

                {data.status === PaymentOutboxStatus.Processing && (
                    <Alert
                        className="mt-4"
                        type="info"
                        showIcon
                        message="Giao dịch đang được worker xử lý, vui lòng chờ."
                    />
                )}
                {data.status === PaymentOutboxStatus.Pending && (
                    <Alert
                        className="mt-4"
                        type="warning"
                        showIcon
                        message="Giao dịch đang chờ worker pick-up (≤ 2 giây)."
                    />
                )}
                {!showRetryButton && isDead && (
                    <Alert
                        className="mt-4"
                        type="warning"
                        showIcon
                        message="Bạn không có quyền thử lại giao dịch Dead. Liên hệ quản trị viên."
                    />
                )}
            </Modal>
        </ConfigProvider>
    );
};

export default DetailModal;
export { paymentOutboxStatusPipe };
